import { useState, useEffect } from "react";
import Head from "next/head";
import { LicenseInfo } from "@mui/x-license-pro";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  ThemeProvider,
  createTheme,
  Box,
  CircularProgress,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { Alert, AlertTitle, Button, Grid } from "@mui/material";

import { useAppStore } from "../stores/appStore.js";
import { useOrgDashboardStore } from "@/stores/orgDashboardStore";
import { LeftNav } from "@/components/ui/LeftNav/leftNav";
import { SearchAppBar } from "@/components/ui/SearchAppBar/searchAppBar";
import { useShowNav } from "@/utils/useShowNav.js";
import { useForceUpdateRedirect } from "@/utils/useForceUpdateRedirect.js";
import { debounce, OPRSessionManagement } from "../utils/sessionManagement";
import { useOnTabVisible } from "@/hooks/useOnTabVisible.js";
import { useEmploymentRecordsStore } from "@/pages/employment/stores/employmentStore";
import { useRouter } from "next/router";
import { CaptchaOverlay } from "@/components/ui/CaptchaOverlay/CaptchaOverlay.jsx";
import { captchaVerification } from "@/services/auth/index.js";
import { SystemNotificationOverlay } from "@/components/ui/SystemNotificationOverlay/SystemNotificationOverlay";

//MUI Pro Key. This key does not need to be secured.
LicenseInfo.setLicenseKey(
  "d2d55865912e0ca441e5c81e205109c4Tz03ODA3MSxFPTE3MzA3MzM5MjYwMDAsUz1wcm8sTE09cGVycGV0dWFsLEtWPTI=",
);

export const theme = createTheme({
  palette: {
    primary: {
      main: "#002B6C",
    },
    secondary: {
      main: "#ffffff",
    },
    background: {
      default: "#FFFFFF",
    },
  },
  typography: {
    fontFamily: "Source Sans Pro, Arial",
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: '#666666',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: '#666666',
          },
        },
      },
    },
  },
});

LicenseInfo.setLicenseKey(
  "63a6c41cf0b2b7f6e5cf3a758b5b2e8aTz04NDQ4NCxFPTE3Mzk3NDExOTQwMDAsUz1wcm8sTE09cGVycGV0dWFsLEtWPTI=",
);

const queryClient = new QueryClient();

export default function App({ Component, pageProps }) {
  const {
    user,
    setUser,
    isLoading,
    fetchNotificationSnapshot,
    fetchShoppingCart,
    initializeCheckSession,
    checkSession,
    countDown,
    countDownId,
    showSessionTimeoutAlert,
    setShowSessionTimeoutAlert,
    isRedirecting,
    isForceUpdate,
    isCaptchaRequired,
    setIsCaptchaRequired,
    captchaKey,
  } = useAppStore();
  const {
    employmentRecords,
    loading: loadingEmploymentRecords,
    fetchEmploymentRecords,
  } = useEmploymentRecordsStore();
  const {
    fetchOrganizations,
    selectedOrganization,
    setSelectedOrganization,
    fetchOrgApplications,
  } = useOrgDashboardStore();

  const showNav = useShowNav();
  useForceUpdateRedirect();
  const [employmentRecordsInit, setEmploymentRecordsInit] = useState(false);

  const handleTabVisible = async () => {
    setUser();
    fetchNotificationSnapshot();
    fetchShoppingCart();
    // If the path is /organizationDashboard, we need to fetch the organization and set it.
    if (router.asPath.includes("organizationDashboard")) {
      if (!selectedOrganization) {
        const orgs = await fetchOrganizations();
        if (orgs) {
          const { organizationid: trainingOrgId } = router.query;
          const org = orgs.find((org) => org.id === trainingOrgId);
          await setSelectedOrganization(org);
        }
      }
      if (router.asPath.includes("applications")) {
        await fetchOrgApplications();
      }
    }
  };
  const router = useRouter();
  useOnTabVisible(handleTabVisible);

  useEffect(() => {
    const fetchRecords = async () => {
      if (!employmentRecordsInit) {
        await fetchEmploymentRecords();
        setEmploymentRecordsInit(true);
      } else {
        const isOnEmploymentPage = router.asPath.startsWith("/employment");
        if (
          !isOnEmploymentPage &&
          !isLoading &&
          employmentRecordsInit &&
          !loadingEmploymentRecords &&
          !isForceUpdate
        ) {
          if (employmentRecords.length === 0) {
            await router.push("/employment/add");
            return;
          }

          if (employmentRecords.length > 0) {
            for (const record of employmentRecords) {
              if (!record?.roles?.length) continue;
              const needsUpdate = record.roles.find(
                (role) =>
                  role?.end_date === "Present" && role?.needs_force_update,
              );
              if (needsUpdate) {
                await router.push(
                  `/employment/${record.id}?force_update=${needsUpdate?.role_id}`,
                );
                return;
              }
            }
          }
        }
      }
    };
    if (user) {
      fetchRecords();
    }
  }, [
    user,
    isLoading,
    employmentRecordsInit,
    loadingEmploymentRecords,
    employmentRecords,
    router,
    fetchEmploymentRecords,
    isForceUpdate,
  ]);

  useEffect(() => {
    // Move eventHandler here if it relies on checkSession
    const eventHandler = debounce(() => {
      if (OPRSessionManagement.hasLogInEventBeenSet()) {
        OPRSessionManagement.LogLoginEvent();
      }
    }, 2000);

    document.body.addEventListener("mousemove", eventHandler);
    document.body.addEventListener("keydown", eventHandler);
    document.body.addEventListener("touchstart", eventHandler);
    document.body.addEventListener("scroll", eventHandler);

    initializeCheckSession(); // Initial call to start checking the session

    return () => {
      document.body.removeEventListener("mousemove", eventHandler);
      document.body.removeEventListener("keydown", eventHandler);
      document.body.removeEventListener("touchstart", eventHandler);
      document.body.removeEventListener("scroll", eventHandler);
    };
  }, [initializeCheckSession]);

  useEffect(() => {
    const setUserData = async () => {
      setUser();
      fetchNotificationSnapshot();
      fetchShoppingCart();
    };
    if (!user) {
      setUserData();
    }
  }, [user, setUser, fetchNotificationSnapshot, fetchShoppingCart]);

  function computeMarginLeft(showNav) {
    if (!showNav) return 0;
    return { xs: 0, md: 30 };
  }

  const captchaSuccess = async (token) => {
    const { status } = await captchaVerification(token);
    if (status) {
      setIsCaptchaRequired(false);
    } else {
      const origin = window.location.origin;

      // Redirect to the root domain
      window.location.assign(origin + "/");
    }
  };

  if (isRedirecting) {
    // this avoids a flash of the old page before redirecting
    return null;
  }
  return (
    <>
      <Head>
        <title>OCCRRA PWA</title>
      </Head>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          {isCaptchaRequired && (
            <CaptchaOverlay onVerify={captchaSuccess} captchaKey={captchaKey} />
          )}
          {showSessionTimeoutAlert && (
            <>
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  zIndex: 9998,
                }}
              ></div>
              <div
                style={{
                  position: "fixed",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 9999,
                }}
              >
                <Alert severity="warning">
                  <AlertTitle>Are you still there?</AlertTitle>
                  Your session will expire in {countDown}
                  <div style={{ marginTop: "10px" }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => {
                        OPRSessionManagement.LogLoginEvent(true);
                        clearTimeout(countDownId);
                        checkSession();
                        setShowSessionTimeoutAlert(false);
                      }}
                    >
                      I&apos;m still here
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      style={{ marginLeft: "10px" }}
                      onClick={() => {
                        OPRSessionManagement.destroySession();
                        window.location =
                          "/user/logout?message=isExpiredUserDidNotConfirm";
                      }}
                    >
                      Logout
                    </Button>
                  </div>
                </Alert>
              </div>
            </>
          )}
          <CssBaseline />
          <Grid container spacing={0} sx={{ width: "100%" }}>
            <Grid item>
              {!isLoading && user && showNav && (
                <LeftNav
                  name={`${user.first_name} ${user.last_name}`}
                  opin={user.opin_formatted}
                  user={user}
                />
              )}
            </Grid>
            <Grid
              item
              sx={{
                flexGrow: 1,
                ml: computeMarginLeft(showNav),
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  display: {
                    xs: "block",
                    sm: "block",
                    md: "none",
                    lg: "none",
                  },
                }}
              >
                {!isLoading && user && <SearchAppBar user={user} />}
              </Box>

              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    mt: 20,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Component {...pageProps} />
                  <SystemNotificationOverlay />
                </>
              )}
            </Grid>
          </Grid>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
}
